import React, { ReactElement } from 'react';
import styled from 'styled-components';

import { DashboardContentContainer } from '../common/DashboardLayout';
import { Header, SubHeader } from '../common/Header';
import { UI_MAX_WIDTH } from '../../../utils/constants';

const Container = styled.div`
  flex-grow: 1;
  height: 100%;
`;

const HeroContainer = styled.div`
  background-color: ${(props) => props.theme.colors.Navy};
  background-image: url('/assets/customer-support-hero-background.webp');
  background-size: auto 188px;
  background-position: bottom right;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 48px;
  padding: 24px;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    padding: 48px;
  }
`;

const CustomerHeader = styled(Header)`
  color: ${props => props.theme.colors.White};
  font-family: ${(props) => props.theme.fonts.grotesk};
  font-size: 32px;
  font-weight: 500;
  letter-spacing: -0.64px;
  line-height: 40px;
  margin-bottom: 8px;
`;

const CustomSubHeader = styled(SubHeader)`
  color: #686C8B;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 0px;
  text-align: center;
`;

const ReleaseNotesContainer = styled.div`
  position: relative;
  padding: 0px 24px 24px 24px;

  @media (min-width: ${(props) => props.theme.breakpoints.Tablet}) {
    padding: 0px 48px 48px 48px;
  }
`;

const ReleaseNoteItem = styled.div`
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.Gray200};
  margin-bottom: 16px;
  padding: 20px 24px;
`;

const ReleaseNoteDateRange = styled.div`
  color: #100847;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.5px;
  line-height: 24px;
  margin-bottom: 12px;
`;

const ReleaseNoteFeature = styled.div`
  color: #686C8B;
  font-size: 14px;
  line-height: 24px;
`;

const TicketNumber = styled.span`
  color: #100847;
  font-weight: 500;
`;

function ReleaseNotes(): ReactElement {
  return (
    <Container>
      <HeroContainer>
        <CustomerHeader>Release Notes</CustomerHeader>
        <CustomSubHeader>
          Stay up to date with the latest features and improvements
        </CustomSubHeader>
      </HeroContainer>

      <DashboardContentContainer maxWidth={UI_MAX_WIDTH}>
        <ReleaseNotesContainer>
          <ReleaseNoteItem>
            <ReleaseNoteDateRange>January 1 – 15, 2026</ReleaseNoteDateRange>
            <ReleaseNoteFeature><TicketNumber>[01/08/2026]</TicketNumber> Improved error handling for high traffic scenarios</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[01/06/2026]</TicketNumber> Enhanced table formatting for currency values</ReleaseNoteFeature>
          </ReleaseNoteItem>

          <ReleaseNoteItem>
            <ReleaseNoteDateRange>December 16 – 31, 2025</ReleaseNoteDateRange>
            <ReleaseNoteFeature><TicketNumber>[12/31/2025]</TicketNumber> Added CSV export for payment data</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/30/2025]</TicketNumber> Design improvements for onboarding and account pages</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/22/2025]</TicketNumber> Improved payment details display</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/19/2025]</TicketNumber> Updated payment status labels</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/16/2025]</TicketNumber> Improved mobile navigation</ReleaseNoteFeature>
          </ReleaseNoteItem>

          <ReleaseNoteItem>
            <ReleaseNoteDateRange>December 1 – 15, 2025</ReleaseNoteDateRange>
            <ReleaseNoteFeature><TicketNumber>[12/15/2025]</TicketNumber> Enhanced mobile table experience</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/11/2025]</TicketNumber> Improved mobile dropdown menus</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/09/2025]</TicketNumber> Added session timeout warning</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/08/2025]</TicketNumber> Added automatic session timeout for security</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/05/2025]</TicketNumber> New account management pages</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/04/2025]</TicketNumber> Enhanced form validation</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/03/2025]</TicketNumber> Improved onboarding validation</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/03/2025]</TicketNumber> Enhanced payment setup workflow</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/02/2025]</TicketNumber> Improved document viewing experience</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[12/01/2025]</TicketNumber> Enhanced file upload security</ReleaseNoteFeature>
          </ReleaseNoteItem>

          <ReleaseNoteItem>
            <ReleaseNoteDateRange>November 16 – 30, 2025</ReleaseNoteDateRange>
            <ReleaseNoteFeature><TicketNumber>[11/21/2025]</TicketNumber> Security settings design update</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/20/2025]</TicketNumber> Improved search and filter visibility</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/20/2025]</TicketNumber> Enhanced navigation dropdown</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/20/2025]</TicketNumber> Account management improvements</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/19/2025]</TicketNumber> Credit limit status indicators</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/18/2025]</TicketNumber> Improved payment authorization flow</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/17/2025]</TicketNumber> Updated pricing information</ReleaseNoteFeature>
            <ReleaseNoteFeature><TicketNumber>[11/10/2025]</TicketNumber> New payment method available</ReleaseNoteFeature>
          </ReleaseNoteItem>
        </ReleaseNotesContainer>
      </DashboardContentContainer>
    </Container>
  );
}

export default ReleaseNotes;
